import {
    GoogleAuthProvider,
    signInAnonymously,
    signInWithEmailAndPassword,
    signInWithPopup,
    UserCredential,
} from 'firebase/auth';

import { AuthState } from '../../interface/authState.interface';
import { authError } from '../../utils/errors.utils';
import { minifyAuth } from '../../utils/minifyAuth.utils';
import { AuthError } from '../../interface';
import { requestPasswordReset } from '../requestPasswordReset';

export const onSuccessLogin = (_set: any, get: any, user: any) => {
    const userMinified = minifyAuth(user);
    get().loginSuccess(userMinified);
};

export const onErrorLogin = (_set: any, get: any, error: any) => {
    const { loginEmailFailure, loginPasswordFailure, loginFailure } = get();

    if (authError.isUserNotFound(error)) {
        loginEmailFailure(AuthError.USER_NOT_FOUND);
    } else if (authError.isInvalidEmail(error)) {
        loginEmailFailure(AuthError.INVALID_EMAIL);
    } else if (authError.isInvalidPassword(error)) {
        loginPasswordFailure(AuthError.WRONG_PASSWORD);
    } else if (authError.isTooManyRequest(error)) {
        loginFailure(AuthError.TOO_MANY_REQUESTS);
    } else if (authError.isUserMismatch(error)) {
        loginFailure(AuthError.USER_MISMATCH);
    } else {
        loginFailure(AuthError.UNKNOWN_ERROR);
    }
};

/**
 * Connexion email / mot de passe. Attend la réponse de Firebase : renvoie les
 * identifiants en cas de succès, `undefined` en cas d'échec (l'erreur est alors
 * déjà posée dans le store). Avant, la promesse se résolvait tout de suite, sans
 * attendre Firebase → la page signin comptait chaque clic comme un succès.
 */
export const loginWithPassword = async (
    set: any,
    get: any,
    email: string,
    password: string
): Promise<UserCredential | undefined> => {
    set((state: AuthState) => ({ ...state, loading: true, errorCode: undefined })); // start loading

    const { firebaseAuth, loginEmailFailure, loginPasswordFailure } = get();

    if (email === '') {
        loginEmailFailure(AuthError.MISSING_EMAIL);
        return undefined;
    }
    if (password === '') {
        loginPasswordFailure(AuthError.MISSING_PASSWORD);
        return undefined;
    }
    let userCredential: UserCredential;
    try {
        userCredential = await signInWithEmailAndPassword(firebaseAuth, email, password);
    } catch (error) {
        onErrorLogin(set, get, error);
        return undefined;
    }
    onSuccessLogin(set, get, userCredential.user);
    return userCredential;
};

export const loginWithGoogle = async (set: any, get: any): Promise<UserCredential | void> => {
    set((state: AuthState) => ({ ...state, loading: true, errorCode: undefined })); // start loading

    const { firebaseAuth } = get();
    const provider = new GoogleAuthProvider();

    const onErrorLoginWithGoogle = () => {
        get().loginFailure(AuthError.UNKNOWN_ERROR);
    };

    try {
        const auth = await signInWithPopup(firebaseAuth, provider);
        // This gives you a Google Access Token. You can use it to access the Google API.
        // const credential = GoogleAuthProvider.credentialFromResult(result);
        // const token = credential?.accessToken;
        // The signed-in user info.
        // const { user } = result;
        // IdP data available using getAdditionalUserInfo(result)
        // ...
        onSuccessLogin(set, get, auth.user);
        return auth;
    } catch (error) {
        // Handle Errors here.
        // const errorCode = error.code;
        // const errorMessage = error.message;
        // The email of the user's account used.
        // const email = error.customData.email;
        // The AuthCredential type that was used.
        // const credential = GoogleAuthProvider.credentialFromError(error);
        // ...
        onErrorLoginWithGoogle();
        return undefined;
    }
};

export const loginAsGuest = async (set: any, get: any) => {
    // Start loading
    const { firebaseAuth } = get();
    set((state: AuthState) => ({ ...state, loading: true, errorCode: undefined }));

    const onErrorLoginAsGuest = () => {
        get().loginFailure(AuthError.UNKNOWN_ERROR);
    };

    signInAnonymously(firebaseAuth)
        .then((user) => {
            // Signed in
            onSuccessLogin(set, get, user);
        })
        .catch(() => {
            onErrorLoginAsGuest();
        });
};

export const handleForgotPassword = async (set: any, get: any) => {
    const { email, changeStatus, loginEmailFailure } = get();

    if (email === '') {
        loginEmailFailure(AuthError.MISSING_EMAIL);
    } else {
        requestPasswordReset(email)
            .then(() => {
                changeStatus('RESET_PASSWORD');
            })
            .catch((error) => {
                onErrorLogin(set, get, error);
            });
    }
};
